import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';

export function useDataTable({ queryKey, fetchRecords, columnsConfig }) {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const records = useQuery([queryKey], fetchRecords);
  const [sorting, setSorting] = useState([]);

  const fuzzyFilter = (row, columnId, value) => {
    const rowValue = row.getValue(columnId);
    if (typeof rowValue !== 'string') {
      return false;
    }
    return rowValue.toLowerCase().includes(value.toLowerCase());
  };

  const columns = useMemo(() => columnsConfig.map((column) => column), []);

  const table = useReactTable({
    data: records.data || [],
    columns,
    state: {
      columnFilters,
      globalFilter,
      sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
  });

  const handleChangePage = (_, newPage) => {
    table.setPageIndex(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    table.setPageSize(parseInt(event.target.value, 10));
    table.setPageIndex(0);
  };

  const clearFilters = () => {
    setGlobalFilter('');
    table
      .getHeaderGroups()
      .forEach((headerGroup) =>
        headerGroup.headers.forEach((header) =>
          header.column.setFilterValue('')
        )
      );
  };

  return {
    table,
    handleChangePage,
    handleChangeRowsPerPage,
    clearFilters,
    globalFilter,
    setGlobalFilter,
    records,
    columnsConfig,
  };
}
